import { batchActions } from 'util/redux'
import bootstrap from 'util/bootstrap'
import metrics from 'util/metrics'
import { redirect, replace } from 'redux-first-router'

import {
  doFetchLabelsByIds,
  doFetchMissingLabelsForCurrentAndPinnedSearch,
} from 'actions/labels'

import { doOpenSearchPage } from 'actions/pages'
import { doFetchTicket } from 'actions/tickets'

import graphql from 'api/graphql'
import { searchTickets } from 'api/search'

import * as types from 'constants/action_types'
import { DEFAULT_SORT_ORDER } from 'constants/defaults'

import {
  oauthTokenSelector,
  fetchingStatusesSelector,
  selectCurrentSortOrder,
  selectDeviceTypeIsDesktop,
} from 'selectors/app'
import { selectAutoLoadFirstTicketInList } from 'ducks/currentUser/selectors'

import { selectDeletedSearch } from 'selectors/left_nav'
import { selectPinnedSearches } from 'selectors/pinned_searches'
import {
  selectCurrentMailbox,
  selectCurrentNavigationSearches,
} from 'selectors/mailboxes'
import { selectIsMerging } from 'selectors/page'
import {
  selectCurrentMergeSearch,
  selectCurrentMergeSearchQueryID,
  selectCurrentMergeSearchQueryObject,
  selectCurrentMergeSearchQueryObjectForAPI,
  selectCurrentTicketSearch,
  selectCurrentTicketSearchQueryId,
  selectCurrentTicketSearchQueryObject,
  selectCurrentTicketSearchQueryObjectForAPI,
  selectCurrentTicketSearchResultTicketIds,
  selectDefaultTicketSearch,
  selectDefaultTicketSearchId,
  selectTicketSearchOperatorValueMap,
  shouldFetchMergeSearchByQueryString,
  shouldFetchTicketSearchByQueryString,
  selectCurrentTicketSearchQueryString,
  shouldFetchIncompleteSearch,
} from 'selectors/search'
import { selectTicketsById } from 'selectors/tickets'
import {
  selectIsOnSearchPageWithoutFirstTicketLoaded,
  selectCurrentPage,
  selectCurrentPayload,
  selectQueryParams,
} from 'selectors/location'
import { selectAccountPreferenceSortByCollaboratorCommentEnabled } from 'selectors/app/selectAccountPreferences'

import debug from 'util/debug'
import { filter } from 'util/objects'
import {
  constructSearchQueryString,
  constructSearchQueryId,
  constructGraphQLSearchQueryObject,
} from 'util/search'

export function doClearNonNamedTicketSearchResults({
  keepCurrent = false,
  refetchListOnNextUpdate = false,
} = {}) {
  return (dispatch, getState) => {
    // Elasticsearch refresh interval is set to one second, which means it is
    // possible for ES to index a change but for it to not be available within 1000ms
    // If we query for updated results instantly we might hit old indexed data, which
    // are incorrect. Which is why we defer clearing of old results by 1 second,
    // guaranteeing that we will not hit previous results
    const state = getState()
    setTimeout(() => {
      dispatch({
        type: types.SEARCH_CLEAR_TICKET_RESULTS,
        data: {
          omitNamedSearches: true,
          valueIdMap: selectTicketSearchOperatorValueMap(state),
          queryIdToKeep: keepCurrent
            ? selectCurrentTicketSearchQueryId(state)
            : undefined,
          refetchListOnNextUpdate,
        },
      })
      // GR: Since we no longer use triggers, we have to manually refresh the
      // ticket list search results
      dispatch(doLoadSearchTickets())
    }, 1000)
  }
}

export function doClearTicketSearchResults(options = {}) {
  return {
    type: types.SEARCH_CLEAR_TICKET_RESULTS,
    data: options,
  }
}

export function doUpdateLatestSearch() {
  return (dispatch, getState) => {
    const queryId = selectCurrentTicketSearchQueryId(getState())
    const isMerging = selectIsMerging(getState())
    return dispatch({
      type: types.STORE_LATEST_SEARCH,
      data: { queryId, isMerging },
    })
  }
}

function markFirstTicketForLoad(dispatch, getState, response) {
  const state = getState()
  if (!selectAutoLoadFirstTicketInList(state)) return null
  if (!selectDeviceTypeIsDesktop(state)) return null
  const firstTicketId =
    response.tickets && response.tickets[0] && response.tickets[0].id
  if (firstTicketId && selectIsOnSearchPageWithoutFirstTicketLoaded(state)) {
    const type = selectCurrentPage(state)
    const query = selectQueryParams(state)
    const payload = selectCurrentPayload(state)

    debug('redirect to search with current first ticket loaded')
    return dispatch(
      redirect({
        type,
        payload,
        meta: { query: { ...query, conversationId: firstTicketId } },
      })
    )
  }
  return null
}

// Attempts to fetch the ticket by given number either from local cache or from
// API. If API fails with a 404, its not treated as an exception.
function doFindTicketByNumber(ticketId) {
  return (dispatch, getState) => {
    if (!ticketId) return Promise.reject('no ticket number given')

    dispatch({
      type: types.SEARCH_BY_TICKET_NUMBER_REQUEST,
      data: { ticketId },
    })

    let findPromise = dispatch(
      doFetchTicket(ticketId, { redirectOn404: false })
    )

    const cached = selectTicketsById(getState())[ticketId]
    if (cached) findPromise = Promise.resolve(cached)

    return findPromise
      .then(ticket => {
        // doFetchTicket wont throw on a 404
        if (ticket.loadFailed) throw new Error('ticket load failed')

        dispatch({
          type: types.SEARCH_BY_TICKET_NUMBER_SUCCESS,
          data: {
            ticketId,
            ticket,
          },
        })
        return ticket
      })
      .catch(err => {
        dispatch({
          type: types.SEARCH_BY_TICKET_NUMBER_FAIL,
          data: { ticketId },
        })
        debug(err)
      })
  }
}

const isValidNumber = num => {
  if (typeof num === 'string' && num[0] === '0') return false
  // 2147483647 is max signed int
  return !isNaN(num) && num < 2147483647
}

const doFetchTicketSearch = (queryId, query, apiQuery, sortOrder, page = 1) => {
  return (dispatch, getState) => {
    const params = { queryId, queryObject: query, apiQuery, page, sortOrder }
    const state = getState()
    const token = oauthTokenSelector(state)
    // TODO: (convo-reordering-after-full-migrations): remove
    const sortByCollaboratorCommentAtEnabled = selectAccountPreferenceSortByCollaboratorCommentEnabled(
      state
    )

    dispatch({
      type: types.SEARCH_REQUEST,
      data: params,
    })

    const promises = [
      searchTickets(
        token,
        apiQuery,
        sortOrder,
        page,
        sortByCollaboratorCommentAtEnabled
      ),
    ]
    if (isValidNumber(queryId)) {
      promises.push(dispatch(doFindTicketByNumber(queryId)))
    }

    // We need the labels loaded fully first so our query IDs are actually IDs
    // and not queryStrings. This can happen when you load a page directly.
    return dispatch(doFetchMissingLabelsForCurrentAndPinnedSearch()).then(
      () => {
        return Promise.all(promises)
          .then(data => {
            const response = data[0]
            const ticketByNr = !isNaN(queryId) ? data[1] : null
            const { tickets } = response

            if (ticketByNr && tickets) tickets.unshift(ticketByNr)

            const queryString = constructSearchQueryString(query)
            metrics.enhanceTimer(`search-load-${queryString}`, {
              responseRuntime: response.runtime,
              responseLatency: response.latency,
            })

            dispatch({
              type: types.SEARCH_SUCCESS,
              data: {
                ...params,
                ...response,
                tickets,
              },
            })

            return response
          })
          .catch(error => {
            debug(error)
            dispatch({
              type: types.SEARCH_ERROR,
              error: true,
              data: {
                ...params,
                message: error.message,
                stack: error.stack,
              },
            })
            return {}
          })
          .then(response => {
            dispatch({
              type: types.SEARCH_COMPLETE,
              data: {
                ...params,
                ...response,
              },
            })
            markFirstTicketForLoad(dispatch, getState, response)

            return response
          })
      }
    )
  }
}

export const doFetchDefaultTicketSearch = () => {
  return (dispatch, getState) => {
    const state = getState()
    const query = selectDefaultTicketSearch(state)
    const queryId = selectDefaultTicketSearchId(state)
    const mailbox = selectCurrentMailbox(state)
    const valueMap = selectTicketSearchOperatorValueMap(state)
    const apiQuery = constructGraphQLSearchQueryObject({ ...query }, valueMap)

    // we need to dispatch SET_CURRENT_TICKET_LIST, because we want to make sure
    // that the default set is set at the currentTicketList. We do it here
    // because we cannot differentiate between a regular search and a default
    // search in the reducer
    dispatch(
      batchActions(doFetchTicketSearch(queryId, query, apiQuery), {
        type: types.SET_CURRENT_TICKET_LIST,
        data: { query: queryId, mailbox, sortOrder: DEFAULT_SORT_ORDER },
      })
    )
  }
}

export function doFetchTicketSearchByQueryString(page = 1) {
  return (dispatch, getState) => {
    const state = getState()

    const searchQueryId = selectCurrentTicketSearchQueryId(state)
    const searchQueryObject = selectCurrentTicketSearchQueryObject(state)
    const sortOrder = selectCurrentSortOrder(state)
    const searchQueryObjectForAPI = selectCurrentTicketSearchQueryObjectForAPI(
      state
    )
    const previousPages = selectCurrentTicketSearch(state)
    const previous = previousPages && previousPages[page]
    if (previous && previous.length > 1) return Promise.resolve() // no need to dispatch

    return dispatch(
      doFetchTicketSearch(
        searchQueryId,
        searchQueryObject,
        searchQueryObjectForAPI,
        sortOrder,
        page
      )
    )
  }
}

// Checks if tickets cached, otherwise fetches from API
export function doLoadMergeSearchByQueryString(page = 1) {
  return (dispatch, getState) => {
    if (!shouldFetchMergeSearchByQueryString(getState())) return false
    return dispatch(doFetchMergeSearchByQueryString(page))
  }
}

export function doFetchMergeSearchByQueryString(page = 1) {
  return (dispatch, getState) => {
    const state = getState()
    const statuses = fetchingStatusesSelector(state)
    if (statuses.fetchTicketSearch) return
    const searchQueryId = selectCurrentMergeSearchQueryID(state)
    const searchQueryObject = selectCurrentMergeSearchQueryObject(state)
    const sortOrder = DEFAULT_SORT_ORDER
    const searchQueryObjectForAPI = selectCurrentMergeSearchQueryObjectForAPI(
      state
    )
    const previousPages = selectCurrentMergeSearch(state)
    const previous = previousPages && previousPages[page]
    // eslint-disable-next-line consistent-return
    if (previous && previous.length > 1) return Promise.resolve() // no need to dispatch

    dispatch({
      type: types.MERGE_SEARCH_REQUEST,
      data: {
        searchQueryId,
        searchQueryObject,
        searchQueryObjectForAPI,
        sortOrder,
        page,
      },
    })

    // eslint-disable-next-line consistent-return
    return dispatch(
      doFetchTicketSearch(
        searchQueryId,
        searchQueryObject,
        searchQueryObjectForAPI,
        page
      )
    ).then(({ currentPage, totalPages, totalCount, tickets }) => {
      dispatch({
        type: types.MERGE_SEARCH_SUCCESS,
        data: {
          searchQueryId,
          searchQueryObject,
          searchQueryObjectForAPI,
          page,
          currentPage,
          totalPages,
          totalCount,
          tickets,
        },
      })
      return { currentPage, totalPages, totalCount, tickets }
    })
  }
}

export function doFetchTicketSearchCountsForPinnedSearches(
  overrides,
  options = {}
) {
  return (dispatch, getState) => {
    const state = getState()
    const starredSearches = selectPinnedSearches(state).map(search => {
      return Object.assign({}, search, overrides)
    })
    // We add in deleted search to starred search, because it is a search item
    // under the closed subsection. But it won't come back in the aggregations
    // because they exclude deleted tickets.
    starredSearches.push(selectDeletedSearch(state))
    return dispatch(doFetchTicketSearchCountsFor(starredSearches, options))
  }
}

export function doFetchTicketSearchCountsFor(searches, options = {}) {
  return (dispatch, getState) => {
    const state = getState()

    dispatch({ type: types.SEARCHES_REQUEST, data: { searches } })

    const starredSearchesForAPI = searches.map(
      constructGraphQLSearchQueryObject
    )
    const starredSearchIds = searches.map(constructSearchQueryId)
    const token = oauthTokenSelector(state)
    const query = `
      query TicketSearchCounts($queries: [SearchQuery]) {
        searches {
          tickets(queries: $queries, bulkCounts: true) {
            metadata {
              totalCount
              timestamp
              mailboxCounts
            }
          }
        }
      }
    `
    const variables = { queries: starredSearchesForAPI }

    return graphql(token, query, variables)
      .then(res => {
        const response = res.json.data.searches
        const counts = response.tickets.reduce((accumulator, result, index) => {
          // eslint-disable-next-line no-param-reassign
          accumulator[starredSearchIds[index]] = {
            count: result.metadata.totalCount || 0,
            timestamp: result.metadata.timestamp,
          }
          return accumulator
        }, {})

        dispatch({
          type: types.SEARCHES_SUCCESS,
          data: {
            response,
            searches: counts,
            metricType: options.metricType,
            trackMetrics: options.trackMetrics,
            isAutomatic: options.isAutomatic,
            isManual: options.isManual,
          },
        })
      })
      .catch(err => {
        debug(err)
        dispatch({ type: types.SEARCHES_FAIL, err })
      })
  }
}

export function doFetchTicketAggregationCountsFor(search, options = {}) {
  const filteredSearch = filter(v => !!v, search)
  const searchQueryString = constructSearchQueryString(filteredSearch)

  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const query = `
      query TicketAggregationCountQuery($searchQuery: SearchQuery) {
        ticketAggregations(query: $searchQuery, groupByMailboxes: true, groups: ["labels.id", "state", "mailbox_id", "assignee_id", "assignee_group_id", "unassigned", "assigned", "is_starred", "is_deleted"]) {
          id
          timestamp
          mailboxId
          counts {
            id
            count
          }
        }
      }
    `

    const variables = {
      searchQuery: filteredSearch,
    }

    dispatch({
      type: types.FETCH_TICKET_AGGREGATION_COUNTS_REQUEST,
      data: {
        filteredSearch,
        searchQueryString,
      },
    })

    return graphql(token, query, variables)
      .then(res => {
        const ticketAggregations = res.json.data.ticketAggregations
        const labelCounts = ticketAggregations.find(x => x.id === 'labels.id')
          .counts
        const labelIds = labelCounts.map(x => x.id).filter(x => !!x && x !== '')

        dispatch({
          type: types.FETCH_TICKET_AGGREGATION_COUNTS_SUCCESS,
          data: {
            filteredSearch,
            searchQueryString,
            ticketAggregations,
            trackMetrics: options.trackMetrics,
            isManual: options.isManual,
            isAutomatic: options.isAutomatic,
          },
        })

        dispatch(doFetchLabelsByIds(labelIds, { reload: false }))

        return ticketAggregations
      })
      .catch(error => {
        dispatch({
          type: types.FETCH_TICKET_AGGREGATION_COUNTS_FAIL,
          data: {
            filteredSearch,
            searchQueryString,
            error,
          },
        })
        // Swallow as not a train smash if some counts dont load.
      })
  }
}

export function doFetchOpenTicketAggregationCountsForEachMailbox(options) {
  return dispatch => {
    dispatch(doFetchTicketAggregationCountsFor({ state: 'opened' }, options))
  }
}

export function doFetchTicketAggregationCountsForUnloadedNavigationSections(
  overrides = {}
) {
  return (dispatch, getState) => {
    const state = getState()
    const statuses = fetchingStatusesSelector(state)
    const currentNavigationSearches = selectCurrentNavigationSearches(state)

    currentNavigationSearches.forEach(search => {
      const overriddenSearch = Object.assign({}, search, overrides)
      const queryString = constructSearchQueryString(overriddenSearch)
      if (statuses[`ticketAggregationCounts-${queryString}`] !== false) {
        dispatch(doFetchTicketAggregationCountsFor(overriddenSearch))
      }
    })
  }
}

export function doUpdateTicketSearchQuery(queryString, options) {
  const {
    amend = false,
    commit = true,
    position,
    reset = false,
    selectionEnd = queryString && queryString.length,
    selectionStart = queryString && queryString.length,
    submit = true,
  } = options
  return dispatch => {
    const trimmedQueryString = queryString && queryString.trim()

    const isTypedSearch = true
    if (trimmedQueryString && submit) {
      dispatch(doOpenSearchPage(trimmedQueryString, isTypedSearch))
    }

    dispatch({
      type: types.UPDATE_CURRENT_TICKET_SEARCH_QUERY,
      data: {
        amend,
        commit,
        position,
        queryString,
        selectionEnd,
        selectionStart,
        submit,
        reset,
        isTypedSearch,
      },
    })
  }
}

export function doToggleListSearchBoxFocused(isFocused) {
  return {
    type: types.TOGGLE_LIST_SEARCH_BOX_STATUS,
    data: { isFocused },
  }
}

export function doLoadSearchTickets() {
  return (dispatch, getState) => {
    let promise = Promise.resolve(true)
    const state = getState()

    if (shouldFetchTicketSearchByQueryString(state)) {
      const queryString = selectCurrentTicketSearchQueryString(state)
      metrics.startOrEnhanceTimer(`search-load-${queryString}`, {
        metric: 'search-load',
        logname: 'main',
        hot: false,
        queryString,
      })
      debug('fetching')
      // We need agents to search by assignee. This is a little janky - ideally
      // we'd just be able to observe the success of the bootstrapQuery
      promise = bootstrap
        .wait()
        .then(() => dispatch(doFetchTicketSearchByQueryString()))
    } else {
      debug('not fetching')
    }

    return promise.then(result => {
      const { term } = state.location.payload
      if (!isNaN(term)) {
        debug('redirecting to ticket page')
        // Open the ticket page directly if its an ID search, and that ticket
        // matches first (sorted) result
        const sortedIds = selectCurrentTicketSearchResultTicketIds(getState())
        if (term === sortedIds[0]) {
          // HACK (jscheel): We have to manually force the latest history entry
          // to be replaced, because RFR's redirect is completely unintuitive and
          // cannot actually replace the entry itself once you are already in
          // the thunk for the route.
          dispatch({
            type: types.TICKET_ID_SEARCH_REDIRECT,
            payload: {
              prev: state.location.prev,
            },
          })
          replace(`/tickets/${term}`)
          // END HACK
        }
      } else {
        debug('no number search. doing nothing')
      }

      return result
    })
  }
}

export function doMarkSearchAsComplete(queryId) {
  return {
    type: types.MARK_SEARCH_AS_COMPLETE,
    data: { queryId },
  }
}

// This is a version of doFetchTicketSearch which is used
// when you expect a bunch of changes to be happening in short succession
// it is both a performance improvement and race condition protection
let debounceTimeout
export function doLoadSearchTicketsDebounced() {
  clearTimeout(debounceTimeout)
  return (dispatch, getState) => {
    const state = getState()
    const queryId = selectCurrentTicketSearchQueryId(state)

    const callback = () => dispatch(doFetchTicketSearchByQueryString())
    debounceTimeout = setTimeout(callback, 3000)
    dispatch(doMarkSearchAsComplete(queryId))
  }
}

let debounceLoad
export function doLoadIncompleteSearch() {
  clearTimeout(debounceLoad)
  return (dispatch, getState) => {
    const state = getState()
    const should = shouldFetchIncompleteSearch(state)
    if (should) {
      const queryId = selectCurrentTicketSearchQueryId(state)
      dispatch(doMarkSearchAsComplete(queryId))
      debounceLoad = setTimeout(() => {
        dispatch(doFetchTicketSearchByQueryString())
      }, 5000)
    }
  }
}
